import { Button } from "@izicvs/ui";
import { cn } from "@izicvs/utils";
import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { useEffect, useMemo, useState } from "react";

import { CardUserTestimonial } from "./card";

// Function to get reviews with translations inside a function as required by lingui
const getReviews = () => [
  {
    name: t`Jack Wilson`,
    username: t`@jackwilson`,
    body: t`Thanks to this resume builder, I landed 3 interviews in a week. The ATS-friendly format made all the difference.`,
    img: "https://avatar.vercel.sh/jackwilson",
    role: t`Full Stack Developer`,
    highlight: true,
  },
  {
    name: t`Marie Durant`,
    username: t`@marie_d`,
    body: t`Intuitive interface and professional templates. My resume caught the attention of a recruiter at Google!`,
    img: "https://avatar.vercel.sh/marie_durant",
    role: t`UX Designer`,
    highlight: false,
  },
  {
    name: t`Thomas Chen`,
    username: t`@thomas_c`,
    body: t`I tripled my response rate after using this service. The customization is exceptional.`,
    img: "https://avatar.vercel.sh/thomas_chen",
    role: t`Product Manager`,
    highlight: true,
  },
  {
    name: t`Sophia Rodriguez`,
    username: t`@sophia_r`,
    body: t`After months of searching, I finally found my dream job thanks to a resume that truly represents me.`,
    img: "https://avatar.vercel.sh/sophia_r",
    role: t`Marketing Specialist`,
    highlight: false,
  },
  {
    name: t`Alexandre Dubois`,
    username: t`@alex_d`,
    body: t`Remarkable tool! The ATS analysis allowed me to optimize my resume to pass automatic filters.`,
    img: "https://avatar.vercel.sh/alex_dubois",
    role: t`Data Scientist`,
    highlight: true,
  },
  {
    name: t`Emma Parker`,
    username: t`@emma_p`,
    body: t`Exceptional customer service and concrete results. I tripled my salary thanks to this platform!`,
    img: "https://avatar.vercel.sh/emma_parker",
    role: t`Finance Manager`,
    highlight: false,
  },
];

const ReviewCard = ({
  img,
  name,
  username,
  body,
  role,
  highlight,
}: {
  img: string;
  name: string;
  username: string;
  body: string;
  role: string;
  highlight?: boolean;
}) => {
  return (
    <figure
      className={cn(
        "relative h-full overflow-hidden rounded-xl border p-5",
        // Responsive width adjustments
        "w-[42vw] sm:w-[40vw] md:w-60 lg:w-52 xl:w-56 2xl:w-64",
        // light styles
        highlight
          ? "border-primary/30 bg-primary/[0.03]"
          : "border-gray-950/[.1] bg-gray-950/[.01]",
        // dark styles
        highlight
          ? "dark:border-primary/40 dark:bg-primary/[0.07]"
          : "dark:border-gray-50/[.1] dark:bg-gray-50/[.05]",
        // shadow
        "shadow-md",
      )}
    >
      <div className="flex flex-row items-center gap-3">
        <img
          className="rounded-full ring-2 ring-primary/20"
          width="42"
          height="42"
          alt={t`Photo of ${name}`}
          src={img}
        />
        <div className="flex flex-col">
          <figcaption className="text-sm font-semibold">{name}</figcaption>
          <p className="text-xs font-medium text-gray-500 dark:text-white/50">{username}</p>
          <p className="mt-0.5 text-xs italic text-primary/80">{role}</p>
        </div>
      </div>

      <blockquote className="relative mt-4 text-sm leading-relaxed">
        {highlight && (
          <svg
            className="absolute -left-1 -top-2 size-6 text-primary/30"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
          </svg>
        )}
        <span className={cn(highlight ? "ml-5" : "")}>{body}</span>
      </blockquote>

      {highlight && (
        <div className="absolute right-2 top-2">
          <svg className="size-5 text-primary" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
          </svg>
        </div>
      )}
    </figure>
  );
};

export function TestimonialsSection() {
  const { i18n } = useLingui();
  const [isMobile, setIsMobile] = useState(false);

  // Compute reviews at render time so the translations follow the active
  // locale instead of being frozen at module load.
  const reviews = useMemo(() => getReviews(), [i18n.locale]);
  const firstRow = useMemo(() => reviews.slice(0, 2), [reviews]);
  const secondRow = useMemo(() => reviews.slice(2, 4), [reviews]);
  const thirdRow = useMemo(() => reviews.slice(4, 6), [reviews]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Initial check
    checkMobile();

    // Add event listener
    window.addEventListener("resize", checkMobile);

    // Cleanup
    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  // Stats
  const stats = [
    { value: t`94%`, label: t`Satisfaction rate` },
    { value: t`3x`, label: t`More responses` },
    { value: t`86%`, label: t`Land an interview` },
  ];

  return (
    <section className="relative py-12" id="testimonials">
      <div className="container mx-auto px-0">
        <div className="relative grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
          {/* Left Column: Title and Description - Always on top for mobile */}
          <div className="relative order-1 flex flex-col px-6 md:px-0 lg:col-span-4">
            <div className="space-y-6">
              <div className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
                <svg className="mr-2 size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {t`Verified testimonials`}
              </div>

              <h2 className="text-3xl font-bold tracking-tight text-neutral-700 dark:text-neutral-300 sm:text-4xl">
                {t`Our users' satisfaction speaks for itself`}
              </h2>

              <p className="text-lg">
                {t`Thousands of professionals have transformed their careers with our ATS-optimized resumes. Discover their stories.`}
              </p>

              {/* Stats cards */}
              <div className="mt-6 grid grid-cols-3 gap-4">
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    className="flex flex-col items-center justify-center rounded-lg border border-gray-100 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-primary/[0.07]"
                  >
                    <span className="text-xl font-bold text-primary md:text-2xl">{stat.value}</span>
                    <span className="mt-1 text-center text-xs text-gray-500 dark:text-gray-400">
                      {stat.label}
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex items-center pt-4">
                <div className="flex -space-x-2">
                  {reviews.slice(0, 4).map((review, index) => (
                    <img
                      key={index}
                      className="inline-block size-10 rounded-full ring-2 ring-background"
                      src={review.img}
                      alt={t`Profile photo of` + ` ${review.name}`}
                    />
                  ))}
                  <div className="inline-flex size-10 items-center justify-center rounded-full bg-primary text-sm font-medium text-white ring-2 ring-background">
                    {t`+5K`}
                  </div>
                </div>
                <div className="ml-4">
                  <div className="flex items-center">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <svg
                        key={i}
                        className="size-5 text-yellow-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                      </svg>
                    ))}
                  </div>
                  <p className="text-sm font-medium">{t`Over 5000 positive reviews`}</p>
                </div>
              </div>

              <Button className="mt-6">
                {t`Start for free`}
                <svg className="ml-2 size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </Button>
            </div>
          </div>

          {/* Right Column: Testimonials */}
          <div
            className={cn(
              "relative order-2 w-full overflow-hidden lg:col-span-8",
              isMobile ? "mt-6 h-[400px] px-0" : "h-[500px]",
            )}
          >
            {isMobile ? (
              // Mobile view with vertical scrolling in two columns
              <div className="flex size-full justify-center gap-0 px-0">
                <div className="relative w-[45%]">
                  <CardUserTestimonial vertical pauseOnHover className="[--duration:40s]">
                    {reviews.map((review) => (
                      <ReviewCard key={review.username} {...review} />
                    ))}
                    {reviews.map((review) => (
                      <ReviewCard key={`dup-${review.username}`} {...review} />
                    ))}
                  </CardUserTestimonial>
                </div>

                <div className="relative w-[45%]">
                  <CardUserTestimonial vertical reverse pauseOnHover className="[--duration:45s]">
                    {[...reviews].reverse().map((review) => (
                      <ReviewCard key={review.username} {...review} />
                    ))}
                    {[...reviews].reverse().map((review) => (
                      <ReviewCard key={`dup-${review.username}`} {...review} />
                    ))}
                  </CardUserTestimonial>
                </div>
              </div>
            ) : (
              // Desktop view - simplified version without perspective
              <div className="flex flex-row items-start justify-center gap-3">
                <div className="flex flex-col gap-4">
                  <CardUserTestimonial pauseOnHover vertical className="[--duration:47s]">
                    {firstRow.map((review) => (
                      <ReviewCard key={review.username} {...review} />
                    ))}
                    {firstRow.map((review) => (
                      <ReviewCard key={`dup-${review.username}`} {...review} />
                    ))}
                  </CardUserTestimonial>
                </div>

                <div className="mt-8 flex flex-col gap-4">
                  <CardUserTestimonial reverse pauseOnHover vertical className="[--duration:45s]">
                    {secondRow.map((review) => (
                      <ReviewCard key={review.username} {...review} />
                    ))}
                    {secondRow.map((review) => (
                      <ReviewCard key={`dup-${review.username}`} {...review} />
                    ))}
                  </CardUserTestimonial>
                </div>

                <div className="mt-16 flex flex-col gap-4">
                  <CardUserTestimonial pauseOnHover vertical className="[--duration:40s]">
                    {thirdRow.map((review) => (
                      <ReviewCard key={review.username} {...review} />
                    ))}
                    {thirdRow.map((review) => (
                      <ReviewCard key={`dup-${review.username}`} {...review} />
                    ))}
                  </CardUserTestimonial>
                </div>
              </div>
            )}

            {/* Simple gradient overlays */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-background to-transparent"></div>
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-background to-transparent"></div>
            <div className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-background to-transparent"></div>
            <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-background to-transparent"></div>
          </div>
        </div>
      </div>
    </section>
  );
}
